import { Alert, Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { executeManualCellTypingMarkerPositivity, getCellTypingMarkerPositivityPlan } from 'api/platform';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { JobParamsJsonTree } from 'components/Pages/Jobs/JobParamsJsonTree';
import { isEqual } from 'es-toolkit';
import { RnaPanelPlanRequest } from 'interfaces/jobs/cellTypingMarkerPositivity';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Permission } from 'interfaces/permissionOption';
import React, { useId, useState } from 'react';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import { usePermissions } from 'utils/usePermissions';
import type { CellTypingRequestedOutputs } from '.';
import {
  getSpotDetectionGeometryError,
  getSpotDetectionScoringGeometry,
  resolveSpotDetectionSetup,
} from '../../PostProcessingAndCalculateFeatures/spotDetectionInputSetup';
import { isPanelConfigurationValid, PanelConfigurationDraft } from './PanelConfigurationEditor';
import { rnaMapCellAssignmentLabel, type RestoredCellTypingExecution } from './previousExecution';

interface Props {
  studyId?: string;
  labId?: string;
  panels: MultiplexPanelView[];
  drafts: Record<string, PanelConfigurationDraft>;
  selectedSlideIds: string[];
  requestedOutputs: CellTypingRequestedOutputs;
  manualSource?: RestoredCellTypingExecution;
  allowManual?: boolean;
}

const getPanelRequests = ({
  panels,
  drafts,
  selectedSlideIds,
  requestedOutputs,
}: Props): RnaPanelPlanRequest[] | null => {
  const selectedIds = new Set(selectedSlideIds);
  if (
    !selectedIds.size ||
    selectedIds.size !== selectedSlideIds.length ||
    !(requestedOutputs.positivity || requestedOutputs.cellTypes)
  )
    return null;
  const requests: RnaPanelPlanRequest[] = [];
  let coveredSlides = 0;
  for (const panel of panels) {
    const targetSlideIds = panel.slides.filter((slide) => selectedIds.has(slide.slideId)).map((slide) => slide.slideId);
    if (!targetSlideIds.length) continue;
    const draft = drafts[panel.panelId];
    if (!draft || !isPanelConfigurationValid(panel, targetSlideIds, draft, requestedOutputs)) return null;
    const rnaProbeIds = new Set(
      panel.channels.flatMap((channel) =>
        channel.saved?.channelModality === 'rna' ? [channel.saved.analysisName] : []
      )
    );
    const mifMarkerIds = new Set(
      panel.channels.flatMap((channel) =>
        channel.saved?.channelModality === 'mif' ? [channel.saved.analysisName] : []
      )
    );
    const hasActiveMifRules = draft.cellRules.some(
      (rule) => mifMarkerIds.has(rule.stainTypeId) && rule.ruleValue !== -1
    );
    const hasMifCutoff = Object.entries(draft.markerThresholds).some(
      ([markerId, value]) => mifMarkerIds.has(markerId) && value !== ''
    );
    if (hasActiveMifRules || hasMifCutoff) return null;
    if (
      requestedOutputs.cellTypes &&
      draft.cellTypeIds.some(
        (cellTypeId) =>
          !draft.cellRules.some(
            (rule) => rule.cellTypeId === cellTypeId && rnaProbeIds.has(rule.stainTypeId) && rule.ruleValue !== -1
          )
      )
    )
      return null;
    const probes: RnaPanelPlanRequest['configuration']['probes'] = [];
    for (const channel of panel.channels) {
      const saved = channel.saved;
      if (saved?.channelModality !== 'rna') continue;
      const setup = { ...draft.spotDetectionSetup, ...draft.perProbeSetup[saved.analysisName] };
      const geometry = getSpotDetectionScoringGeometry(setup);
      if (
        getSpotDetectionGeometryError(setup) ||
        (geometry.compartment === 'cell' && setup.segmentationDilationsUm?.length !== 1)
      )
        return null;
      probes.push({
        probeId: saved.analysisName,
        positivityThreshold: Number(draft.probeThresholds[saved.analysisName]),
        ...geometry,
      });
    }
    if (!probes.length) return null;
    requests.push({
      panelId: panel.panelId,
      revision: panel.revision!,
      targetSlideIds,
      configuration: {
        probes,
        cellRules: requestedOutputs.cellTypes
          ? draft.cellTypeIds.map((targetCellTypePath) => ({
              targetCellTypePath,
              rules: Object.fromEntries(
                draft.cellRules
                  .filter((rule) => rule.cellTypeId === targetCellTypePath && rnaProbeIds.has(rule.stainTypeId))
                  .map((rule) => [rule.stainTypeId, rule.ruleValue])
              ),
            }))
          : [],
      },
    });
    coveredSlides += targetSlideIds.length;
  }
  return coveredSlides === selectedIds.size ? requests : null;
};

const getReviewBlockReason = (props: Props, canExecute: boolean): string | null => {
  if (!props.studyId || !props.labId) return 'Study and lab scope are required.';
  if (!canExecute) return 'You need calculate-features permission to review this run.';
  if (!(props.requestedOutputs.positivity || props.requestedOutputs.cellTypes)) return 'Select an output to review.';
  if (!props.selectedSlideIds.length) return 'Select at least one panel slide.';
  const selectedIds = new Set(props.selectedSlideIds);
  const panels = props.panels.filter((panel) => panel.slides.some((slide) => selectedIds.has(slide.slideId)));
  const coveredIds = new Set(
    panels.flatMap((panel) =>
      panel.slides.filter((slide) => selectedIds.has(slide.slideId)).map((slide) => slide.slideId)
    )
  );
  if (coveredIds.size !== selectedIds.size) return 'Some selected slides are outside the current panel scope.';
  for (const panel of panels) {
    const targetSlideIds = panel.slides.filter((slide) => selectedIds.has(slide.slideId)).map((slide) => slide.slideId);
    const draft = props.drafts[panel.panelId];
    if (!draft || !panel.saved || !panel.revision || draft.panelRevision !== panel.revision) {
      return 'Save the current panel definition before reviewing this run.';
    }
    const rnaProbeIds = new Set(
      panel.channels.flatMap((channel) =>
        channel.saved?.channelModality === 'rna' ? [channel.saved.analysisName] : []
      )
    );
    const mifMarkerIds = new Set(
      panel.channels.flatMap((channel) =>
        channel.saved?.channelModality === 'mif' ? [channel.saved.analysisName] : []
      )
    );
    if (
      draft.cellRules.some((rule) => mifMarkerIds.has(rule.stainTypeId) && rule.ruleValue !== -1) ||
      Object.entries(draft.markerThresholds).some(([markerId, value]) => mifMarkerIds.has(markerId) && value !== '')
    ) {
      return 'This review compiles RNA rules only. Active mIF classifier rules or cutoff overrides need the binary-classifier flow.';
    }
    if (props.requestedOutputs.cellTypes) {
      if (!draft.cellTypeIds.length) return 'Add at least one cell class in the Cell Type column.';
      if (
        draft.cellTypeIds.some(
          (cellTypeId) =>
            !draft.cellRules.some(
              (rule) => rule.cellTypeId === cellTypeId && rnaProbeIds.has(rule.stainTypeId) && rule.ruleValue !== -1
            )
        )
      ) {
        return 'Set at least one active rule for each selected cell class.';
      }
    }
    if (!isPanelConfigurationValid(panel, targetSlideIds, draft, props.requestedOutputs)) {
      return 'Correct the cutoffs, spot-count geometry or H-score bins before reviewing.';
    }
  }
  return null;
};

export const RnaActionReview = (props: Props) => {
  const titleId = useId();
  const { hasPermission } = usePermissions();
  const { data: cellTypes } = useCellTypeOptions();
  const [isOpen, setIsOpen] = useState(false);
  const [reviewedKey, setReviewedKey] = useState('');
  const planning = useMutation(getCellTypingMarkerPositivityPlan, { retry: false, onSuccess: () => setIsOpen(true) });
  const execution = useMutation(executeManualCellTypingMarkerPositivity, { retry: false });
  const panels = getPanelRequests(props);
  const request =
    props.studyId && props.labId && panels ? { studyId: props.studyId, labId: props.labId, panels } : null;
  const requestKey = JSON.stringify({
    request,
    outputs: props.requestedOutputs,
    source: props.manualSource?.sourceJobId,
    drafts: props.drafts,
  });
  const isCurrent = Boolean(
    request && reviewedKey === requestKey && JSON.stringify(planning.variables) === JSON.stringify(request)
  );
  const unchangedCountSetup = panels?.every(({ panelId }) => {
    const draft = props.drafts[panelId];
    const previous = props.manualSource?.panelDrafts[panelId];
    if (!previous) return false;
    const previousSetup = resolveSpotDetectionSetup(previous.spotDetectionSetup, draft.spotDetectionSetup.rnaCountMode);
    return (
      isEqual(draft.spotDetectionSetup.segmentationCompartments, previousSetup.segmentationCompartments) &&
      isEqual(draft.spotDetectionSetup.segmentationDilationsUm, previousSetup.segmentationDilationsUm) &&
      (!props.requestedOutputs.hScores ||
        (isEqual(draft.spotDetectionSetup.countBins, previous.spotDetectionSetup.countBins) &&
          isEqual(draft.perProbeSetup, previous.perProbeSetup)))
    );
  });
  const manualRequest =
    request && request.panels.length === 1 && props.manualSource && props.allowManual && unchangedCountSetup
      ? {
          studyId: request.studyId,
          labId: request.labId,
          sourceJobId: props.manualSource.sourceJobId,
          panel: request.panels[0],
          requestedOutputs: props.requestedOutputs,
        }
      : null;
  const reviewBlockReason = getReviewBlockReason(props, hasPermission(Permission.RunCalculateFeatures));
  const error = planning.error as {
    message?: string;
    response?: { body?: { message?: unknown; detail?: unknown } };
  } | null;
  const responseError = error?.response?.body?.message || error?.response?.body?.detail || error?.message;
  const errorText = typeof responseError === 'string' ? responseError : 'RNA actions could not be generated.';
  const executionError = execution.error as {
    message?: string;
    response?: { body?: { message?: unknown; detail?: unknown } };
  } | null;
  const executionDetail =
    executionError?.response?.body?.message || executionError?.response?.body?.detail || executionError?.message;

  return (
    <>
      {isCurrent && planning.isError && <Alert severity="error">{errorText}</Alert>}
      <Stack spacing={0.5} alignItems="flex-start">
        <Button
          variant="contained"
          color="primary"
          sx={{ alignSelf: 'flex-start' }}
          disabled={
            !request || planning.isLoading || execution.isLoading || !hasPermission(Permission.RunCalculateFeatures)
          }
          onClick={() => {
            if (request && !execution.isLoading) {
              execution.reset();
              setReviewedKey(requestKey);
              planning.mutate(request);
            }
          }}
        >
          {planning.isLoading ? 'Reviewing...' : 'Review run'}
        </Button>
        {reviewBlockReason && (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280 }}>
            {reviewBlockReason}
          </Typography>
        )}
      </Stack>
      {isOpen && isCurrent && planning.data && (
        <Dialog open fullWidth maxWidth="md" aria-labelledby={titleId} onClose={() => setIsOpen(false)}>
          <DialogChromeHeader titleId={titleId} title="Generated RNA actions" onClose={() => setIsOpen(false)} />
          <DialogContent dividers>
            <Stack spacing={2}>
              {execution.isError && (
                <Alert severity="error">
                  {typeof executionDetail === 'string' ? executionDetail : 'The manual run could not be submitted.'}
                </Alert>
              )}
              {execution.isSuccess && <Alert severity="success">Manual run submitted</Alert>}
              {planning.data.map((plan) => (
                <Box key={plan.scope.panelId}>
                  <Typography variant="h3">
                    {props.panels.find((panel) => panel.panelId === plan.scope.panelId)?.name}
                  </Typography>
                  <Typography>
                    {plan.scope.targetSlideIds.length} selected{' '}
                    {plan.scope.targetSlideIds.length === 1 ? 'slide' : 'slides'}
                  </Typography>
                  <Box component="ol">
                    {plan.postprocessing.map((action, actionIndex) => (
                      <li key={actionIndex}>
                        {action.action_name === 'map_cells'
                          ? rnaMapCellAssignmentLabel(
                              action,
                              cellTypes?.find((cellType) => cellType.id.split('.').pop() === action.output_name)
                                ?.displayName ||
                                action.output_name ||
                                'cell'
                            )
                          : 'Probe positivity'}
                      </li>
                    ))}
                  </Box>
                  <details>
                    <summary>Canonical action configuration</summary>
                    <JobParamsJsonTree data={plan} />
                  </details>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogChromeFooter>
            <Button
              variant="contained"
              color="primary"
              disabled={
                !manualRequest ||
                !hasPermission(Permission.ViewJobs) ||
                !hasPermission(Permission.RunCalculateFeatures) ||
                execution.isLoading ||
                execution.isSuccess
              }
              onClick={() => {
                if (manualRequest) execution.mutate(manualRequest);
              }}
            >
              {execution.isLoading ? 'Submitting...' : 'Run manual'}
            </Button>
            <Button color="inherit" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogChromeFooter>
        </Dialog>
      )}
    </>
  );
};
